import { useEffect, useState, useRef } from "react";
import { Spin, Tag, Carousel } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useCity } from "../context/CityContext";
import {
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";

const ExploreTopThingsToDoCountry = ({
  groupName,
  filterBy = "city",
  countryName = "",
}) => {
  const [toursPackage, setToursPackage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // Name returned by Strapi
  const [apiGroupName, setApiGroupName] = useState(groupName || "");

  // Location name returned by Strapi / Context
  const [locationName, setLocationName] = useState("");

  const carouselRef = useRef(null);

  const navigate = useNavigate();
  const { selectedCity } = useCity();

  const location = useLocation();

  const isLandingPage = location.pathname === "/";

  // =====================================================
  // RESET + FETCH WHEN FILTER CHANGES
  // =====================================================

  useEffect(() => {
    setToursPackage([]);
    setPage(1);
    setPageCount(1);
    setApiGroupName(groupName || "");

    if (filterBy === "country") {
      if (countryName) {
        fetchTours(1);
      } else {
        setLocationName("");
      }

      return;
    }

    // CITY MODE
    if (selectedCity?.slug) {
      setLocationName(selectedCity.name || "");
      fetchTours(1);
    } else {
      setLocationName("");
    }
  }, [
    selectedCity?.slug,
    countryName,
    filterBy,
    groupName,
  ]);

  // =====================================================
  // FETCH PRODUCTS
  // =====================================================

  const fetchTours = async (pageNumber = 1) => {
    const isCountryMode = filterBy === "country";

    // Validation
    if (isCountryMode && !countryName) {
      console.log("Country name is missing");
      return;
    }

    if (!isCountryMode && !selectedCity?.slug) {
      console.log("Selected city is missing");
      return;
    }

    try {
      if (pageNumber === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // =====================================================
      // BASE API PARAMS
      // =====================================================

      const params = {
        // Product fields
        "fields[0]": "title",
        "fields[1]": "slug",
        "fields[2]": "final_price",
        "fields[3]": "old_price",
        "fields[4]": "save_price",
        "fields[5]": "duration",

        // Images
        "populate[images][fields][0]": "url",

        // Sub product
        "populate[sub_product][fields][0]": "slug",

        // City
        "populate[city][fields][0]": "name",
        "populate[city][fields][1]": "slug",

        // Country
        "populate[city][populate][country][fields][0]":
          "name",

        "populate[city][populate][country][fields][1]":
          "slug",

        // Product groups
        "populate[product_groups][fields][0]":
          "name",

        "populate[product_groups][fields][1]":
          "slug",

        // Pagination
        "pagination[page]": pageNumber,
        "pagination[pageSize]": 4,
      };

      // =====================================================
      // COUNTRY MODE
      // =====================================================

      if (isCountryMode) {
        // Landing page:
        // ALL product details from ALL cities
        // belonging to this country

        params["filters[city][country][name][$eq]"] =
          countryName;
      }

      // =====================================================
      // CITY MODE
      // =====================================================

      else {
        // Inner page:
        // Only selected city products

        params["filters[city][slug][$eq]"] =
          selectedCity.slug;

        // Only add group filter when supplied
        if (groupName) {
          params["filters[product_groups][name][$eq]"] =
            groupName;
        }
      }

 

      // =====================================================
      // API CALL
      // =====================================================

      const response = await axiosInstance.get(
        "/product-details",
        {
          params,
        }
      );

      const newData =
        response.data?.data || [];

      const pagination =
        response.data?.meta?.pagination;

 

      // =====================================================
      // UPDATE PRODUCTS
      // =====================================================

      setToursPackage((previous) =>
        pageNumber === 1
          ? newData
          : [...previous, ...newData]
      );

      // =====================================================
      // UPDATE PAGINATION
      // =====================================================

      setPage(pageNumber);

      setPageCount(
        pagination?.pageCount || 1
      );

    } catch (error) {
      console.error(
        "Explore API Error:",
        error
      );

      console.error(
        "API Error Response:",
        error?.response?.data
      );

      setToursPackage([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // =====================================================
  // NEXT
  // =====================================================

  const handleNext = async () => {
    if (loadingMore) {
      return;
    }

    if (page < pageCount) {
      const nextPage =
        page + 1;

      await fetchTours(
        nextPage
      );

      setTimeout(() => {
        carouselRef.current?.next();
      }, 200);

      return;
    }

    carouselRef.current?.next();
  };

  // =====================================================
  // PREVIOUS
  // =====================================================

  const handlePrev = () => {
    carouselRef.current?.prev();
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin
          size="large"
          tip={`Loading ${apiGroupName ||
            groupName ||
            "Tours"
            }...`}
        />
      </div>
    );
  }

  // =====================================================
  // VALIDATION
  // =====================================================

  if (
    filterBy === "city" &&
    !selectedCity?.slug
  ) {
    return null;
  }

  if (
    filterBy === "country" &&
    !countryName
  ) {
    return null;
  }

  // =====================================================
  // DISPLAY NAME
  // =====================================================

  const finalGroupName =
    apiGroupName ||
    groupName ||
    "Explore";

  const finalLocationName =
    locationName ||
    (filterBy === "country"
      ? countryName
      : selectedCity?.name || "");

  // =====================================================
  // VIEW MORE SLUG
  // =====================================================

  const viewMoreSlug =
    groupName
      ?.toLowerCase()
      .trim()
      .replace(/\s+/g, "-");

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div>

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="flex justify-between items-center mb-6">

        <div>

          <h2 className="!text-xl sm:!text-2xl !font-medium font-normal text-primary">



            <span className="text-primary">
              {finalGroupName}
            </span>

            {finalLocationName && (
              <span className="text-danger">
                {" "}in {finalLocationName}
              </span>
            )}

          </h2>

          <p className="font-normal text-sm">
            Culture, nature, thrills, and
            record-breaking experiences
          </p>

        </div>

        {/* VIEW MORE */}
        {!isLandingPage && (
          <a
            onClick={() =>
              navigate(
                `/view-all/${viewMoreSlug}`
              )
            }
            className="
            font-normal
            text-xs
            cursor-pointer
          "
          >
            View More{" "}
            <DoubleRightOutlined />
          </a>
        )}
      </div>

      {/* =================================================
          CAROUSEL
      ================================================= */}

      <div className="relative custom-slider">

        {/* LEFT */}

        {toursPackage.length > 0 && (
          <button
            type="button"
            onClick={handlePrev}
            className="
              absolute
              -left-5 max-sm:left-0
              top-1/2
              -translate-y-1/2
              z-10
              w-10
              h-10
              bg-white
              shadow-lg
              rounded-full
              flex
              items-center
              justify-center
              hover:scale-110
              transition
            "
          >
            <LeftOutlined />
          </button>
        )}

        {/* RIGHT */}

        {toursPackage.length > 0 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={loadingMore}
            className="
              absolute
              -right-5 max-sm:right-0
              top-1/2
              -translate-y-1/2
              z-10
              w-10
              h-10
              bg-white
              shadow-lg
              rounded-full
              flex
              items-center
              justify-center
              hover:scale-110
              transition
              disabled:opacity-50
            "
          >
            {loadingMore ? (
              "..."
            ) : (
              <RightOutlined />
            )}
          </button>
        )}

        {/* =================================================
            CAROUSEL
        ================================================= */}

        <Carousel
          key={toursPackage.length}
          ref={carouselRef}
          dots={false}
          infinite={false}
          slidesToShow={Math.min(
            4,
            toursPackage.length
          )}
          slidesToScroll={1}
          responsive={[
            {
              breakpoint: 1024,
              settings: {
                slidesToShow:
                  Math.min(
                    3,
                    toursPackage.length
                  ),
              },
            },
            {
              breakpoint: 768,
              settings: {
                slidesToShow:
                  Math.min(
                    2,
                    toursPackage.length
                  ),
              },
            },
            {
              breakpoint: 480,
              settings: {
                slidesToShow: 1,
              },
            },
          ]}
        >

          {/* =================================================
              PRODUCTS
          ================================================= */}

          {toursPackage.length > 0 ? (

            toursPackage.map(
              (tour) => {

                const title =
                  tour?.title ||
                  "Untitled Tour";

                const subSlug =
                  tour?.sub_product
                    ?.slug ||
                  "not-found";

                const detailSlug =
                  tour?.slug ||
                  title
                    .toLowerCase()
                    .replace(
                      /\s+/g,
                      "-"
                    ) ||
                  "details";

                const finalPrice =
                  tour?.final_price ??
                  0;

                const oldPrice =
                  tour?.old_price;

                const savePrice =
                  tour?.save_price;

                const duration =
                  tour?.duration ||
                  "N/A";

                const imageUrl =
                  tour?.images?.[0]
                    ?.url ||
                  "https://via.placeholder.com/400x250?text=No+Image";

                return (
                  <div
                    key={tour.id}
                    className="px-1 h-full"
                  >

                    <div
                      onClick={() =>
                        navigate(
                          `/${subSlug}/${detailSlug}`
                        )
                      }
                      className="
                        bg-white
                        cursor-pointer
                        rounded-xl
                        overflow-hidden
                        relative
                        shadow-custom
                        hover:shadow-2xl
                        h-full
                      "
                    >

                      {/* IMAGE */}

                      <div className="relative">

                        {/* OVERLAY */}

                        <div
                          className="
                            absolute
                            overflow-hidden
                            bg-black/20
                            w-full
                            h-full
                            z-10
                          "
                        >

                          <div
                            className="
                              absolute
                              bottom-0
                              left-0
                              right-0
                              p-4
                              text-left
                            "
                          >

                            <div
                              className="
                                text-base
                                font-semibold
                                text-white
                              "
                            >
                              AED{" "}
                              {finalPrice}
                            </div>

                            {oldPrice && (
                              <div
                                className="
                                  line-through
                                  text-white
                                  text-md
                                "
                              >
                                AED{" "}
                                {oldPrice}
                              </div>
                            )}

                            {savePrice && (
                              <div
                                className="
                                  text-white
                                  text-md
                                "
                              >
                                SAVE AED{" "}
                                {savePrice}
                              </div>
                            )}

                          </div>

                        </div>

                        {/* IMAGE */}

                        <img
                          src={`${process.env.REACT_APP_API_URL}${imageUrl}`}
                          alt={title}
                          className="
                            w-full
                            h-[170px]
                            object-cover
                          "
                        />

                        {/* RATING */}

                        <div
                          className="
                            bg-primary
                            absolute
                            bottom-[-25px]
                            right-6
                            rounded-full
                            shadow-lg
                            text-white
                            text-sm
                            flex
                            justify-center
                            w-[80px]
                            h-[80px]
                            z-20
                          "
                        >

                          <div className="
                            text-center
                            mt-2
                          ">

                            <div className="text-3xl">
                              ★
                            </div>

                            <div className="text-xs">
                              4.9 (523)
                            </div>

                          </div>

                        </div>

                      </div>

                      {/* CONTENT */}

                      <div className="p-5 pt-3">

                        <div
                          className="
                            mt-3
                            mb-2
                            text-muted
                            text-[11px]
                            font-medium
                          "
                        >
                          Tour Type

                          <Tag
                            className="
                              bg-blue-100
                              text-black
                              border-0
                              text-[11px]
                              ml-2
                            "
                          >
                            Standard
                          </Tag>

                        </div>

                        <div
                          className="
                            font-medium
                            text-black
                            text-md
                            line-clamp-2
                          "
                        >
                          {title}
                        </div>

                        <div
                          className="
                            text-primary
                            mt-2
                            font-medium
                          "
                        >
                          {duration}
                        </div>

                      </div>

                    </div>

                  </div>
                );
              }
            )

          ) : (

            <p className="text-center text-muted">
              No {finalGroupName} tours
              available in{" "}
              {finalLocationName}.
            </p>

          )}

        </Carousel>

      </div>

    </div>
  );
};

export default ExploreTopThingsToDoCountry;